import { comparePositions, intervalsOverlap } from './annotationCollision';

const clone = value => JSON.parse(JSON.stringify(value));

const positionTuple = (annotation, edge) => edge === 'start'
  ? [annotation.startMeasure, annotation.startBeat, annotation.startSubdivision || 0]
  : [annotation.endMeasure, annotation.endBeat, annotation.endSubdivision || 0];

const annotationInterval = annotation => ({
  start: positionTuple(annotation, 'start'),
  end: positionTuple(annotation, 'end'),
});

const failure = reason => ({ success: false, reason });
const EPSILON = 1e-6;

const isRepresentable = (value, toValue, fromValue) => (
  Math.abs(toValue(fromValue(value)) - value) <= EPSILON
);

export const snapMovementDelta = (delta, movementStep) => {
  if (!(movementStep > 0)) return delta;
  const direction = Math.sign(delta);
  return direction >= 0
    ? Math.floor((delta + EPSILON) / movementStep) * movementStep
    : Math.ceil((delta - EPSILON) / movementStep) * movementStep;
};

const areEdgesRepresentable = (edges, delta, toValue, fromValue) => {
  for (const edge of edges) {
    if (!isRepresentable(edge + delta, toValue, fromValue)) return false;
  }
  return true;
};

const snapRepresentableDelta = ({ delta, edges, movementStep, toValue, fromValue }) => {
  if (!(movementStep > 0)) return delta;
  const direction = Math.sign(delta);
  let snapped = snapMovementDelta(delta, movementStep);
  while (
    Math.abs(snapped) > EPSILON &&
    !areEdgesRepresentable(edges, snapped, toValue, fromValue)
  ) {
    snapped -= direction * movementStep;
  }
  return areEdgesRepresentable(edges, snapped, toValue, fromValue)
    ? snapped
    : null;
};

export const orderAnnotations = annotations => [...annotations].sort((left, right) => {
  const startOrder = comparePositions(positionTuple(left, 'start'), positionTuple(right, 'start'));
  if (startOrder !== 0) return startOrder;

  const endOrder = comparePositions(positionTuple(left, 'end'), positionTuple(right, 'end'));
  if (endOrder !== 0) return endOrder;
  return String(left.id).localeCompare(String(right.id));
});

export const selectAnnotationRange = ({ annotations, trackId, anchorId, targetId }) => {
  const ordered = orderAnnotations(annotations.filter(annotation => annotation.trackId === trackId));
  const anchorIndex = ordered.findIndex(annotation => annotation.id === anchorId);
  const targetIndex = ordered.findIndex(annotation => annotation.id === targetId);
  if (anchorIndex < 0 || targetIndex < 0) return targetIndex < 0 ? [] : [targetId];

  const first = Math.min(anchorIndex, targetIndex);
  const last = Math.max(anchorIndex, targetIndex);
  return ordered.slice(first, last + 1).map(annotation => annotation.id);
};

export const selectMarqueeAnnotations = ({
  annotations,
  trackId,
  startValue,
  endValue,
  toValue,
}) => {
  const minimum = Math.min(startValue, endValue);
  const maximum = Math.max(startValue, endValue);

  return orderAnnotations(annotations.filter(annotation => {
    if (annotation.trackId !== trackId) return false;
    const start = toValue(positionTuple(annotation, 'start'));
    const end = toValue(positionTuple(annotation, 'end'));
    return end >= minimum && start <= maximum;
  })).map(annotation => annotation.id);
};

export const planGroupMove = ({
  annotations,
  selectedIds,
  proposedDelta,
  trackId,
  sheetStart,
  sheetEnd,
  toValue,
  fromValue,
  movementStep,
}) => {
  const selectedIdSet = new Set(selectedIds);
  const selected = orderAnnotations(annotations.filter(annotation => selectedIdSet.has(annotation.id)));
  if (
    selected.length === 0 ||
    selected.length !== selectedIdSet.size ||
    selected.some(annotation => annotation.trackId !== trackId)
  ) {
    return failure('invalid-selection');
  }

  const unselected = annotations.filter(annotation => (
    annotation.trackId === trackId && !selectedIdSet.has(annotation.id)
  ));
  let minimumDelta = sheetStart - Math.min(
    ...selected.map(annotation => toValue(positionTuple(annotation, 'start')))
  );
  let maximumDelta = sheetEnd - Math.max(
    ...selected.map(annotation => toValue(positionTuple(annotation, 'end')))
  );

  selected.forEach(annotation => {
    const selectedStart = toValue(positionTuple(annotation, 'start'));
    const selectedEnd = toValue(positionTuple(annotation, 'end'));
    unselected.forEach(neighbor => {
      const neighborStart = toValue(positionTuple(neighbor, 'start'));
      const neighborEnd = toValue(positionTuple(neighbor, 'end'));
      if (neighborStart >= selectedEnd) {
        maximumDelta = Math.min(maximumDelta, neighborStart - selectedEnd);
      }
      if (neighborEnd <= selectedStart) {
        minimumDelta = Math.max(minimumDelta, neighborEnd - selectedStart);
      }
    });
  });

  const clampedDelta = Math.min(Math.max(proposedDelta, minimumDelta), maximumDelta);
  const edges = selected.flatMap(annotation => [
    toValue(positionTuple(annotation, 'start')),
    toValue(positionTuple(annotation, 'end')),
  ]);
  const delta = snapRepresentableDelta({
    delta: clampedDelta, edges, movementStep, toValue, fromValue,
  });
  if (delta === null) return failure('grid-mismatch');
  return {
    success: true,
    delta,
    intervals: selected.map(annotation => ({
      id: annotation.id,
      interval: {
        start: fromValue(toValue(positionTuple(annotation, 'start')) + delta),
        end: fromValue(toValue(positionTuple(annotation, 'end')) + delta),
      },
    })),
  };
};

export const planBlockMove = ({ id, ...options }) => planGroupMove({
  ...options,
  selectedIds: [id],
});

export const planBlockResize = ({
  annotations,
  id,
  direction,
  proposedDelta,
  trackId,
  sheetStart,
  sheetEnd,
  minimumWidth = 0,
  toValue,
  fromValue,
  movementStep,
}) => {
  const annotation = annotations.find(candidate => candidate.id === id);
  if (!annotation || annotation.trackId !== trackId || !['left', 'right'].includes(direction)) {
    return failure('invalid-selection');
  }

  const original = annotationInterval(annotation);
  const start = toValue(original.start);
  const end = toValue(original.end);
  const duration = end - start;
  const neighbors = annotations.filter(candidate => (
    candidate.trackId === trackId && candidate.id !== id
  ));
  let minimumDelta;
  let maximumDelta;
  let movingEdge;

  if (direction === 'right') {
    minimumDelta = minimumWidth - duration;
    maximumDelta = sheetEnd - end;
    neighbors.forEach(neighbor => {
      const neighborStart = toValue(positionTuple(neighbor, 'start'));
      if (neighborStart >= end) maximumDelta = Math.min(maximumDelta, neighborStart - end);
    });
    movingEdge = end;
  } else {
    minimumDelta = sheetStart - start;
    maximumDelta = duration - minimumWidth;
    neighbors.forEach(neighbor => {
      const neighborEnd = toValue(positionTuple(neighbor, 'end'));
      if (neighborEnd <= start) minimumDelta = Math.max(minimumDelta, neighborEnd - start);
    });
    movingEdge = start;
  }

  if (maximumDelta < minimumDelta) return failure('minimum-width');
  const clampedDelta = Math.min(Math.max(proposedDelta, minimumDelta), maximumDelta);
  const delta = snapRepresentableDelta({
    delta: clampedDelta,
    edges: [movingEdge],
    movementStep,
    toValue,
    fromValue,
  });
  if (delta === null) return failure('grid-mismatch');

  const interval = direction === 'right'
    ? { start: original.start, end: fromValue(end + delta) }
    : { start: fromValue(start + delta), end: original.end };
  const collision = neighbors.some(neighbor => intervalsOverlap(interval, annotationInterval(neighbor)));
  if (collision) return failure('collision');

  return { success: true, delta, interval };
};

export const planPaste = ({
  clipboard,
  sheetId,
  sessionId,
  trackId,
  playheadValue,
  sheetEnd,
  annotations,
  toValue,
  fromValue,
}) => {
  if (
    !clipboard?.blocks?.length ||
    clipboard.sheetId !== sheetId ||
    clipboard.sessionId !== sessionId ||
    clipboard.trackId !== trackId
  ) {
    return failure('invalid-clipboard');
  }

  const sourceBlocks = orderAnnotations(clipboard.blocks);
  const earliestStart = Math.min(
    ...sourceBlocks.map(annotation => toValue(positionTuple(annotation, 'start')))
  );
  const proposed = sourceBlocks.map(source => {
    const sourceStart = toValue(positionTuple(source, 'start'));
    const sourceEnd = toValue(positionTuple(source, 'end'));
    const start = playheadValue + sourceStart - earliestStart;
    const end = start + sourceEnd - sourceStart;
    return {
      source: clone(source),
      values: { start, end },
      interval: { start: fromValue(start), end: fromValue(end) },
    };
  });

  if (proposed.some(item => (
    Math.abs(toValue(item.interval.start) - item.values.start) > EPSILON ||
    Math.abs(toValue(item.interval.end) - item.values.end) > EPSILON
  ))) return failure('grid-mismatch');

  if (proposed.some(item => (
    toValue(item.interval.start) < 0 || toValue(item.interval.end) > sheetEnd
  ))) {
    return failure('sheet-boundary');
  }

  const existing = annotations
    .filter(annotation => annotation.trackId === trackId)
    .map(annotationInterval);
  const hasExistingCollision = proposed.some(item => (
    existing.some(interval => intervalsOverlap(item.interval, interval))
  ));
  const hasInternalCollision = proposed.some((item, index) => (
    proposed.slice(index + 1).some(other => intervalsOverlap(item.interval, other.interval))
  ));
  if (hasExistingCollision || hasInternalCollision) return failure('collision');

  return {
    success: true,
    intervals: proposed.map(({ values, ...item }) => item),
  };
};
